import { normalizeLangTag, primaryLangSubtag } from '../lang.ts';

/**
 * **Which language the interface is in, and which locale its numbers and dates are in** —
 * the two are not the same question, which is the whole reason this file exists.
 *
 * Before this, every `Intl` call in `apps/web` passed `undefined` as its locale, i.e. *the browser's*.
 * The copy beside it was English. So on a German browser the playlist header read
 * **"Updated vor 2 Wochen"** and the settings page **"Last agreed jetzt"** — an English verb in front
 * of a German phrase, in one sentence, three times over. It is not a translation gap: no amount of
 * translating fixes a sentence assembled from two locales.
 *
 * The rule this file states is therefore:
 *
 * > **One locale governs both.** The formatting locale is a *regional variant of the copy's own
 * > language*, never a different language.
 *
 * That leaves one degree of freedom worth keeping, and {@link resolveLocale} keeps it: a reader whose
 * system is `en-GB` gets British formatting of English copy (`13k`, `31/07/2026`) while a reader whose
 * system is `de-DE` gets the plain `en` defaults (`13K`, `July 31, 2026`) — because `de-DE` is not a
 * variant of English and borrowing its separators would be the same mixing in smaller print.
 *
 * R1 holds here as strictly as anywhere: no `Intl`, no `Locale`, plain string work over
 * {@link normalizeLangTag}. Core decides *which* locale; the platform is what owns `Intl`.
 */

/**
 * The languages this build has a complete catalogue for.
 *
 * A union rather than `string`, so it is the compiler that answers "is there copy for this?" — the same
 * trick `ApiErrorKind`'s exhaustive copy tables play one layer up. Adding a language is: write the
 * catalogue, add the id here, and every `Record<UiLocaleId, …>` in the tree stops compiling until it is
 * handled.
 */
export type UiLocaleId = 'en' | 'de';

export const UI_LOCALES: readonly UiLocaleId[] = ['en', 'de'];

/** What an unreadable, unavailable or absent choice resolves to. Not a preference — a floor. */
export const FALLBACK_UI_LOCALE: UiLocaleId = 'en';

/**
 * A stored or pulled tag → a language this build can actually render in, or `null` for "no choice".
 *
 * Matched on the **primary subtag**, so the instance's `en-US` and a browser's `en-GB` both land on
 * `en`: a regional variant is not a different catalogue, and refusing it would turn "English (United
 * Kingdom)" into "no preference" for no reason a reader could see.
 *
 * A tag this build has no copy for returns `null` rather than the fallback, and the difference is
 * load-bearing in both directions. Read from storage it means *follow the system*, which resolves to
 * English anyway. Read from the account it means **leave the local setting alone** — `settings/sync.ts`'s
 * one rule — so an account whose UI language is Français keeps it, and a Visious push does not quietly
 * overwrite it with English on the way past. The example used to be Deutsch and stopped being one when
 * `strings/de/` shipped: a language this build *can* render is a value, and a value is pulled and does
 * overwrite the local choice.
 */
export function normalizeUiLocale(input: unknown): UiLocaleId | null {
  if (typeof input !== 'string') return null;
  const primary = primaryLangSubtag(input);
  if (primary === '') return null;
  return UI_LOCALES.find((id) => id === primary) ?? null;
}

/** Where the resolved language came from, so a settings page can say so rather than imply a choice. */
export type UiLocaleSource =
  /** The reader picked it. */
  | 'setting'
  /** Their system asked for it and this build has it. */
  | 'system'
  /** Their system asked for something this build has no copy for. */
  | 'fallback';

export interface ResolvedLocale {
  /** Which catalogue to render. */
  readonly ui: UiLocaleId;
  /**
   * What `Intl` is handed — always a variant of {@link ui}, never another language. May equal it.
   */
  readonly formatting: string;
  readonly source: UiLocaleSource;
}

export interface LocaleInput {
  /** `VisiousSettings.uiLocale` — `null` is "follow the system", which is the default. */
  readonly setting: UiLocaleId | null;
  /** The system's ranked preferences, most-wanted first — `navigator.languages` on web. */
  readonly system: readonly string[];
}

/**
 * The one locale decision, made once per page load.
 *
 * Three steps, and the third is the one that does the real work:
 *
 * 1. An explicit setting wins. It is already a `UiLocaleId`, so there is nothing to match.
 * 2. Otherwise the first system tag this build has a catalogue for wins — ranked, so a
 *    `['de-DE', 'en-GB', 'en']` browser lands on English rather than on the fallback *by accident*.
 * 3. **The formatting locale is then the most specific system tag that shares the chosen language**, and
 *    the plain language id when none does. This is what keeps `en-GB` readers on British forms without
 *    ever letting a `de-DE` system reach an English sentence.
 *
 * Deliberately not `Intl.LocaleMatcher`-shaped: there is no negotiation to do beyond a primary-subtag
 * match while the available set is a handful of languages, and R1 bans the machinery that would do it.
 */
export function resolveLocale(input: LocaleInput): ResolvedLocale {
  const wanted = input.setting;
  const fromSystem = wanted === null ? firstAvailable(input.system) : null;
  const ui = wanted ?? fromSystem ?? FALLBACK_UI_LOCALE;
  const source: UiLocaleSource =
    wanted !== null ? 'setting' : fromSystem !== null ? 'system' : 'fallback';

  return { ui, formatting: formattingTagFor(ui, input.system), source };
}

function firstAvailable(system: readonly string[]): UiLocaleId | null {
  for (const tag of system) {
    const match = normalizeUiLocale(tag);
    if (match !== null) return match;
  }
  return null;
}

/**
 * The system's own spelling of the chosen language, when it has one — `en-GB` for a British browser
 * reading English copy.
 *
 * Normalised first, so two spellings of one locale cannot become two locales, and then re-cased: the
 * settings page **shows this tag**, and `en-gb` in a sentence reads as a bug rather than as a locale.
 */
function formattingTagFor(ui: UiLocaleId, system: readonly string[]): string {
  for (const tag of system) {
    const normalized = normalizeLangTag(tag);
    if (normalized !== '' && primaryLangSubtag(normalized) === ui) return canonicalCase(normalized);
  }
  return ui;
}

/**
 * BCP-47's own casing convention, which is a *convention* rather than a comparison rule: language
 * lower, script Title, region UPPER, everything else lower. Four lines because that is all it is —
 * nothing here parses the tag, and `Intl` would have accepted the lower-cased form either way.
 */
function canonicalCase(normalized: string): string {
  return normalized
    .split('-')
    .map((part, index) => {
      if (index === 0) return part;
      if (part.length === 4) return part[0]?.toUpperCase() + part.slice(1);
      if (part.length === 2 || /^\d{3}$/.test(part)) return part.toUpperCase();
      return part;
    })
    .join('-');
}
